/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
// @mui
import { Box, Button, Card, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
// other libraries
import { isArray, isEmpty } from 'lodash';
// Components
import { Iconify } from '../../../components';
import { TreatmentAccordion } from '../../../components/leadTreatment';
import TreatmentEditPopover from '../../../pages/dashboard/tables/AllLeadTable/TreatmentEditPopover';
import LeadTreatmentPlansSection from './LeadTreatmentPlansSection';
// hook
import { useAddTreatment, useTabs } from '../../../hooks';
import { resolveTreatmentTabId } from '../../../utils/leadTreatmentSelect';
import { getLeadStatusUi } from '../../../utils/leadStatusUi';
// redux
import { getActivities } from '../../../redux/slices/activity';
import { useSelector, dispatch } from '../../../redux/store';
import { getAppointment, followUpSlice, clearLead, getLeadReminderData } from '../../../redux/slices/lead';
import { getLeadTreatmentsList, setCurrentTreatmentDataGlobal } from '../../../redux/slices/multiTreatment';
// style
import { taskCard } from '../../../styles/leadTaskReminder';
import { treatmentDetailCardStyle, treatmentTabNewTreatmentBtnStyle } from '../../../styles/treatmentTabStyle';

const TreatmentsDetails = ({
    leadDetail,
    reminder,
    reloadTaskCounter,
    counter,
    handleLoading,
    selectTreatmentData,
}) => {

    const { currentTab, onChangeTab } = useTabs('');
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { followLead } = useSelector((state) => state.lead);
    const { treatmentsList } = useSelector((state) => state.multiTreatment);
    const [searchParams] = useSearchParams();
    const lastRequestedRef = useRef(undefined);

    const [leadAppointment, setLeadAppointment] = useState('');
    const [loading, setLoading] = useState(false);
    const [anchorEditTreatment, setAnchorEditTreatment] = useState(null);

    useEffect(() => {
        if (!leadDetail?.id) return;
        dispatch(getAppointment(practiceId, leadDetail.id));
        dispatch(getLeadTreatmentsList({ leadId: leadDetail.id }));
        reloadTaskCounter({ userId: '', id: leadDetail.id, additionalFilter: '', CompletedTask: false });
        setLoading(true);
        setTimeout(() => setLoading(false), 2000);
    }, []);

    useEffect(() => {
        if (followLead) {
            setLeadAppointment(followLead?.lead_appointment);
        }
    }, [followLead]);

    // Seed the Treatments tab. A NEW explicit request (Overview-pushed selection or
    // a changed ?treatmentId=) wins for deep-link correctness; otherwise the current
    // tab is preserved across refetches (add/edit treatment). Reads ?treatmentId=
    // directly into local tab state — no dependency on active treatment.
    useEffect(() => {
        if (isEmpty(treatmentsList)) return;
        const requestedId = selectTreatmentData?.id ?? searchParams.get('treatmentId');
        // Default to a single treatment's full detail (most-recent) so the user sees info by
        // default; the dropdown still offers "All" + other treatments. Deep-link ?treatmentId= wins.
        const honourRequestedFirst = requestedId != null && String(requestedId) !== String(lastRequestedRef.current);
        const nextTab = resolveTreatmentTabId(treatmentsList, currentTab, requestedId, { honourRequestedFirst });
        if (requestedId != null) lastRequestedRef.current = requestedId;
        if (nextTab !== undefined && String(nextTab) !== String(currentTab)) onChangeTab('', nextTab);
    }, [treatmentsList, selectTreatmentData?.id, searchParams]);

    const { toggle: EditTreatment, onOpen: onEditTreatment, onClose: onCloseEditTreatment } = useAddTreatment();

    const handleAddTreatment = (event) => {
        onEditTreatment();
        setAnchorEditTreatment(event.currentTarget);
    };

    const handleClickAwayLister = (e, popup1Ref) => {
        if (popup1Ref && popup1Ref.current) {
            if (!popup1Ref.current?.contains(e.target)) {
                onCloseEditTreatment();
                dispatch(followUpSlice(leadDetail.id, practiceId, 'LeadDetail'));
                dispatch(getLeadTreatmentsList({ leadId: leadDetail.id }));
                return;
            }
            return;
        }
        onCloseEditTreatment();
        dispatch(followUpSlice(leadDetail.id, practiceId, 'LeadDetail'));
        dispatch(getLeadTreatmentsList({ leadId: leadDetail.id }));
    };

    const handleReloadAPI = (status) => {
        document.body.classList.remove('points-event');
        if (status?.toLowerCase?.() === 'finish') {
            handleClickAwayLister();
            dispatch(clearLead());
            dispatch(setCurrentTreatmentDataGlobal(null));
            if (leadDetail && leadDetail.id) {
                dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, '', leadDetail.id, ''));
                dispatch(getLeadReminderData(practiceId, 'LeadDetail', 1, 0, '', leadDetail?.id, '', '', 'Graterthan'));
                dispatch(followUpSlice(leadDetail.id, practiceId, 'LeadDetail'));
                dispatch(getActivities(practiceId, leadDetail.id, 10, 0));
            }
        }
    };

    return (
        <Card sx={{ ...taskCard('LeadDetail'), padding: '24px' }}>
            {/* One clean row: treatment selector (left) inline with New-treatment (right). No h2. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {isArray(treatmentsList) && treatmentsList.map((t) => {
                        const ui = getLeadStatusUi(t.status);
                        const isFocused = String(t.id) === String(currentTab);
                        return (
                            <Box
                                key={t.id}
                                onClick={() => onChangeTab('', t.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '10px', cursor: 'pointer',
                                    border: `1px solid ${isFocused ? ui.color : 'rgba(145,158,171,0.24)'}`,
                                    bgcolor: isFocused ? alpha(ui.color, 0.08) : '#fff',
                                }}
                            >
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ui.color }} />
                                <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1B2330' }}>{t.treatment}</Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{`£${t.price}`}</Typography>
                            </Box>
                        );
                    })}
                </Stack>
                <Button
                    sx={treatmentTabNewTreatmentBtnStyle}
                    variant='outlined'
                    onClick={(event) => handleAddTreatment(event)}
                >
                    <Iconify sx={{ mr: '8px' }} icon={'eva:plus-fill'} width={20} height={20} /> New treatment
                </Button>
            </Box>
            {/* Stack the single accordion full-width. treatmentDetailCardStyle's flex+space-between
                was for the old two-column (tabs | detail) layout; with the tabs now in the top
                selector row the detail is the only child, so we override to a full-width column. */}
            <Card sx={{ ...treatmentDetailCardStyle, flexDirection: 'column', justifyContent: 'flex-start', gap: 0 }}>
                {/* Focused treatment — full detail (clean, full-width) */}
                {isArray(treatmentsList) && treatmentsList.length > 0 &&
                    treatmentsList.map((data, index) => (
                        String(data?.id) === String(currentTab) && (
                            <TreatmentAccordion
                                key={data.id}
                                data={data}
                                index={index}
                                leadDetail={leadDetail}
                                leadAppointment={leadAppointment}
                                reminder={reminder}
                                reloadTaskCounter={reloadTaskCounter}
                                counter={counter}
                                handleLoading={handleLoading}
                                loading={loading}
                                selectTreatmentData={selectTreatmentData}
                            />
                        )
                    ))}
            </Card>

            {/* Treatment Plans — moved from the Forms tab. A treatment plan is the
                priced, shareable artefact of a treatment, so it lives alongside
                treatments rather than in the patient-form tab. */}
            {leadDetail?.id && <LeadTreatmentPlansSection leadDetail={leadDetail} />}

            {EditTreatment && (
                <TreatmentEditPopover
                    handleReloadAPI={handleReloadAPI}
                    open={EditTreatment}
                    anchorEl={anchorEditTreatment}
                    handleClickAwayLister={handleClickAwayLister}
                    leadId={leadDetail?.id}
                    isAddTreatment
                />
            )}
        </Card>
    );
};

TreatmentsDetails.propTypes = {
    leadDetail: PropTypes.any,
    reminder: PropTypes.any,
    reloadTaskCounter: PropTypes.func,
    counter: PropTypes.array,
    handleLoading: PropTypes.func,
    selectTreatmentData: PropTypes.object,
};

export default TreatmentsDetails;